'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Zap, TrendingUp, CreditCard, Lock, Check, Loader2, ChevronDown, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '../../../lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


type BoostType = 'boost' | 'double_vote';

interface PaymentFields {
  cardNumber: string;
  cardName: string;
  expiry: string;
  cvv: string;
}

interface UserPost {
  id: string;
  content: string;
  agreeCount: number;
  disagreeCount: number;
}

const PRODUCTS = [
  {
    type: 'boost' as BoostType,
    label: 'Push to Top',
    price: 4.99,
    icon: Zap,
    color: 'text-boost',
    bg: 'bg-boost-bg',
    border: 'border-amber-200',
    selectedBorder: 'border-amber-400',
    description: 'Back your claim. Pin it to the top of the feed for 24 hours — maximum eyes on your side.',
    perks: ['Pinned to top of feed', 'Boost badge shown', 'Lasts 24 hours', 'Instant activation'],
  },
  {
    type: 'double_vote' as BoostType,
    label: 'Double Vote Weight',
    price: 2.99,
    icon: TrendingUp,
    color: 'text-primary',
    bg: 'bg-accent',
    border: 'border-indigo-200',
    selectedBorder: 'border-primary',
    description: 'Push your side harder. Every agree vote counts as 2 — climb the rankings and win the argument.',
    perks: ['2× vote multiplier', '2× Votes badge shown', 'Lasts 48 hours', 'Instant activation'],
  },
];

function formatCardNumber(val: string) {
  return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(val: string) {
  const clean = val.replace(/\D/g, '').slice(0, 4);
  if (clean.length >= 3) return clean.slice(0, 2) + '/' + clean.slice(2);
  return clean;
}

export default function BoostPaymentContent() {
  const { user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const lockedPostId = searchParams.get('postId');
  const supabase = createClient();

  const [selectedType, setSelectedType] = useState<BoostType>('boost');
  const [selectedPostId, setSelectedPostId] = useState<string>('');
  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [userPosts, setUserPosts] = useState<UserPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PaymentFields>();

  const cardNumberVal = watch('cardNumber', '');
  const expiryVal = watch('expiry', '');

  useEffect(() => {
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    const fetchUserPosts = async () => {
      setPostsLoading(true);
      const { data, error } = await supabase
        .from('posts')
        .select('id, content, agree_count, disagree_count')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data.map((p) => ({
          id: p.id,
          content: p.content,
          agreeCount: p.agree_count,
          disagreeCount: p.disagree_count,
        }));
        setUserPosts(mapped);
        if (lockedPostId) {
          setSelectedPostId(lockedPostId);
        } else if (mapped.length > 0) {
          setSelectedPostId(mapped[0].id);
        }
      }
      setPostsLoading(false);
    };
    fetchUserPosts();
  }, [user?.id]);

  const selectedProduct = PRODUCTS.find((p) => p.type === selectedType)!;
  const selectedPost = userPosts.find((p) => p.id === selectedPostId);

  const onSubmit = async (_data: PaymentFields) => {
    if (!user || !selectedPostId) return;
    setLoading(true);
    setSubmitError('');
    try {
      // TODO: Replace this simulated payment with a real payment gateway (e.g. Stripe, PayPal)
      // before going live. Currently any card input is accepted and treated as successful.
      await new Promise((resolve) => setTimeout(resolve, 1500)); // simulate network delay

      // Apply boost via SECURITY DEFINER RPC (bypasses RLS on transactions table)
      const { data: rpcData, error: rpcError } = await supabase.rpc('apply_boost', {
        p_user_id: user.id,
        p_post_id: selectedPostId,
        p_boost_type: selectedType,
      });

      if (rpcError) throw rpcError;
      if (rpcData?.error) throw new Error(rpcData.error);

      setConfirmed(true);
    } catch (err: any) {
      setSubmitError(err.message ?? 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (confirmed && selectedPost) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
        <div className="w-16 h-16 rounded-full bg-agree-bg flex items-center justify-center mx-auto mb-4">
          <Check size={28} className="text-agree" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {selectedType === 'boost' ? "Your claim is live at the top." : "Double power activated."}
        </h2>
        <p className="text-sm text-muted-foreground mb-1">
          {selectedType === 'boost' ? "You backed your claim. Don't lose it." : 'Every vote now counts double. Push your side.'}
        </p>
        <div className="bg-muted rounded-xl p-4 mb-6 text-left mt-4">
          <p className="text-xs text-muted-foreground mb-1 font-medium">Your post</p>
          <p className="text-sm text-foreground leading-relaxed line-clamp-2">{selectedPost.content}</p>
        </div>
        <div className="flex flex-col gap-3">
          <Link
            href="/"
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold text-sm px-6 py-2.5 rounded-lg hover:bg-indigo-600 transition-all duration-150 active:scale-95"
          >
            <ArrowLeft size={14} />
            Back to Feed
          </Link>
          <Link
            href="/"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center justify-center gap-2 bg-muted text-foreground font-semibold text-sm px-6 py-2.5 rounded-lg hover:bg-muted/80 transition-all duration-150 active:scale-95"
          >
            Say it again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Step 1: Select post */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <p className="text-sm font-semibold text-foreground mb-3">1. Select your post</p>
        {postsLoading ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 size={14} className="animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading your posts...</span>
          </div>
        ) : userPosts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No posts found. <Link href="/" className="text-primary underline">Be the first to post.</Link></p>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={() => setPostDropdownOpen(!postDropdownOpen)}
              className="w-full flex items-start justify-between gap-3 bg-background border border-input rounded-lg px-3 py-2.5 text-left hover:border-primary transition-all duration-150"
            >
              <p className="text-sm text-foreground line-clamp-2 flex-1 leading-relaxed">{selectedPost?.content ?? 'Select a post'}</p>
              <ChevronDown size={16} className={`text-muted-foreground flex-shrink-0 mt-0.5 transition-transform duration-150 ${postDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {postDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-20 overflow-hidden">
                {userPosts.map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    onClick={() => { setSelectedPostId(post.id); setPostDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-0 ${selectedPostId === post.id ? 'bg-accent' : ''}`}
                  >
                    <p className="text-sm text-foreground line-clamp-2 leading-relaxed">{post.content}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-agree font-medium tabular-nums">{post.agreeCount} agree</span>
                      <span className="text-xs text-disagree font-medium tabular-nums">{post.disagreeCount} disagree</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Select product */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <p className="text-sm font-semibold text-foreground mb-3">2. Choose your boost</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRODUCTS.map((product) => {
            const Icon = product.icon;
            const isSelected = selectedType === product.type;
            return (
              <button
                key={`product-${product.type}`}
                type="button"
                onClick={() => setSelectedType(product.type)}
                className={`text-left p-4 rounded-xl border-2 transition-all duration-150 ${
                  isSelected ? `${product.selectedBorder} ${product.bg}` : `border-border hover:border-muted-foreground/30`
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`flex items-center gap-2 ${product.color}`}>
                    <Icon size={18} />
                    <span className="font-semibold text-sm">{product.label}</span>
                  </div>
                  <span className="font-bold text-lg text-foreground tabular-nums">${product.price}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{product.description}</p>
                <ul className="space-y-1">
                  {product.perks.map((perk) => (
                    <li key={`perk-${product.type}-${perk}`} className="flex items-center gap-1.5 text-xs text-foreground">
                      <Check size={11} className="text-agree flex-shrink-0" />
                      {perk}
                    </li>
                  ))}
                </ul>
                {isSelected && (
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary">
                    <Check size={12} />
                    Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 3: Payment */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-foreground">3. Payment details</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Lock size={11} />
            Secure checkout
          </div>
        </div>

        {/* Order summary */}
        <div className="bg-muted rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">You're backing your claim with</p>
            <p className="text-sm font-semibold text-foreground">{selectedProduct.label}</p>
          </div>
          <p className="text-xl font-bold text-foreground tabular-nums">${selectedProduct.price}</p>
        </div>

        {submitError && (
          <p className="text-xs text-disagree mb-3">{submitError}</p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" suppressHydrationWarning>
          <div suppressHydrationWarning>
            <label htmlFor="card-name" className="block text-sm font-semibold text-foreground mb-1.5">
              Name on card
            </label>
            <input
              id="card-name"
              type="text"
              placeholder="Your Name"
              autoComplete="cc-name"
              suppressHydrationWarning
              className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150"
              {...register('cardName', { required: 'Cardholder name is required.' })}
            />
            {errors.cardName && <p className="text-xs text-disagree mt-1">{errors.cardName.message}</p>}
          </div>

          <div suppressHydrationWarning>
            <label htmlFor="card-number" className="block text-sm font-semibold text-foreground mb-1.5">
              Card number
            </label>
            <div className="relative" suppressHydrationWarning>
              <input
                id="card-number"
                type="text"
                inputMode="numeric"
                placeholder="1234 5678 9012 3456"
                autoComplete="cc-number"
                value={cardNumberVal}
                suppressHydrationWarning
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150 tabular-nums"
                {...register('cardNumber', {
                  required: 'Card number is required.',
                  onChange: (e) => setValue('cardNumber', formatCardNumber(e.target.value)),
                })}
              />
              <CreditCard size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            {errors.cardNumber && <p className="text-xs text-disagree mt-1">{errors.cardNumber.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3" suppressHydrationWarning>
            <div suppressHydrationWarning>
              <label htmlFor="expiry" className="block text-sm font-semibold text-foreground mb-1.5">
                Expiry
              </label>
              <input
                id="expiry"
                type="text"
                inputMode="numeric"
                placeholder="MM/YY"
                autoComplete="cc-exp"
                value={expiryVal}
                suppressHydrationWarning
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150 tabular-nums"
                {...register('expiry', {
                  required: 'Expiry is required.',
                  onChange: (e) => setValue('expiry', formatExpiry(e.target.value)),
                })}
              />
              {errors.expiry && <p className="text-xs text-disagree mt-1">{errors.expiry.message}</p>}
            </div>
            <div suppressHydrationWarning>
              <label htmlFor="cvv" className="block text-sm font-semibold text-foreground mb-1.5">
                CVV
              </label>
              <input
                id="cvv"
                type="text"
                inputMode="numeric"
                placeholder="123"
                autoComplete="cc-csc"
                maxLength={4}
                suppressHydrationWarning
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150 tabular-nums"
                {...register('cvv', {
                  required: 'CVV is required.',
                })}
              />
              {errors.cvv && <p className="text-xs text-disagree mt-1">{errors.cvv.message}</p>}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || userPosts.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold text-sm py-3 rounded-xl hover:bg-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98]"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Lock size={14} />
            )}
            {loading ? 'Locking it in...' : `${selectedType === 'boost' ? 'Back my claim — $' + selectedProduct.price : 'Push my side — $' + selectedProduct.price}`}
          </button>
        </form>
      </div>
    </div>
  );
}